import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const medicalSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/medical-malpractice#webpage`,
      url: `${siteUrl}/medical-malpractice`,
      name: 'Medical Malpractice Attorney Indiana | Fishers Medical Negligence Lawyer | Jun Law Firm',
      description:
        'Injured by medical negligence in Indiana? Jun Law Firm holds doctors and hospitals accountable for surgical errors, misdiagnosis, and birth injuries. Free case review. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/medical-malpractice#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Medical Malpractice', item: `${siteUrl}/medical-malpractice` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/medical-malpractice#service`,
      name: 'Jun Law Firm - Medical Malpractice Attorney Indiana',
      url: `${siteUrl}/medical-malpractice`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Indiana medical malpractice attorney representing victims of surgical errors, misdiagnosis, medication mistakes, and birth injuries. Bilingual legal team. Free case evaluation.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the statute of limitations for medical malpractice in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'In Indiana, medical malpractice claims generally must be filed within 2 years of the date the malpractice occurred. However, exceptions may apply. Contact an attorney as soon as possible to protect your rights.',
          },
        },
        {
          '@type': 'Question',
          name: 'What must be proven in a medical malpractice case?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'To prove medical malpractice you must establish: (1) a doctor-patient relationship existed, (2) the provider breached the standard of care, (3) the breach directly caused your injury, and (4) you suffered measurable damages.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does immigration status affect my ability to file a medical malpractice claim in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Indiana law protects all patients regardless of immigration status. You have the right to seek compensation for medical negligence without your status affecting your legal rights.',
          },
        },
      ],
    },
  ],
};

export default function MedicalMalpractice() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const caseTypes = [
    {
      number: 'I',
      title: 'Surgical Errors',
      description: 'Wrong-site surgery, anesthesia mistakes, surgical instrument left inside patient, and post-operative complications due to negligence.'
    },
    {
      number: 'II',
      title: 'Misdiagnosis',
      description: 'Failure to diagnose cancer, heart disease, stroke, or other serious conditions leading to delayed treatment and worsened outcomes.'
    },
    {
      number: 'III',
      title: 'Medication Errors',
      description: 'Wrong medication prescribed, incorrect dosage, dangerous drug interactions, and pharmacy dispensing errors.'
    },
    {
      number: 'IV',
      title: 'Birth Injuries',
      description: 'Cerebral palsy, Erb\'s palsy, brain damage, and other injuries to mother or baby during pregnancy, labor, or delivery.'
    },
    {
      number: 'V',
      title: 'Hospital Negligence',
      description: 'Infections, bedsores, falls, inadequate monitoring, and other injuries caused by substandard hospital care.'
    },
    {
      number: 'VI',
      title: 'Emergency Room Errors',
      description: 'Failure to properly triage, delayed treatment, misread test results, and discharge of patients needing admission.'
    }
  ];

  const provingMalpractice = [
    {
      step: '01',
      title: 'Doctor-Patient Relationship',
      description: 'We establish that you had a formal relationship with the healthcare provider who treated you.'
    },
    {
      step: '02',
      title: 'Breach of Standard of Care',
      description: 'We prove the provider failed to meet the accepted medical standard of care for your condition.'
    },
    {
      step: '03',
      title: 'Causation',
      description: 'We demonstrate that the provider\'s negligence directly caused your injury or worsened condition.'
    },
    {
      step: '04',
      title: 'Damages',
      description: 'We document all physical, emotional, and financial harm you suffered as a result of the malpractice.'
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Medical Malpractice Attorney Indiana | Fishers Medical Negligence Lawyer | Jun Law Firm"
        description="Injured by medical negligence in Indiana? Jun Law Firm holds doctors and hospitals accountable for surgical errors, misdiagnosis, and birth injuries. Free case review. (317) 701-2768."
        keywords="medical malpractice attorney Indiana, medical negligence lawyer Fishers, surgical error attorney, birth injury lawyer Indiana, misdiagnosis attorney"
        canonicalPath="/medical-malpractice"
        schema={medicalSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-lavender-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Medical Negligence Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Medical Malpractice <em className="font-light italic">Attorney</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Injured by medical negligence in Indiana? You deserve justice regardless of your immigration status. Jun Law Firm holds negligent doctors, hospitals, and healthcare providers accountable for substandard care that harms patients.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Free Case Review
              </button>
              <a 
                href="tel:+13177012768"
                className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Call: (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Case Types */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Our Expertise
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Types of <em className="italic">Medical Malpractice</em> Cases
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Medical errors can have devastating consequences. We represent victims of all types of healthcare negligence in Indiana.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {caseTypes.map((caseType, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {caseType.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {caseType.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {caseType.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Proving Malpractice */}
      <section className="py-24 bg-lavender-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Legal Process
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              How We <em className="italic">Prove</em> Medical Malpractice
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Medical malpractice cases require expert testimony and thorough investigation. Our team works with leading medical experts to build your case.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            {provingMalpractice.map((element, index) => (
              <article key={index} className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  {element.step}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {element.title}
                </h3>
                <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {element.description}
                </p>
              </article>
            ))}
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-900 p-12">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
              Expert Team
            </div>
            <h3 className="text-3xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Medical <em className="italic">Expert Network</em>
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Dr. Emily Chen, our legal counsel with both J.D. and M.D. degrees, leads our medical malpractice team. She works with board-certified physicians, surgeons, and medical specialists who review your case and provide expert testimony to prove negligence.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>01</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Board-certified medical experts in all specialties</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>02</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Thorough medical record review and analysis</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>03</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Expert testimony preparation and trial support</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-xs tracking-wider text-gray-500 mr-4 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>04</span>
                <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Life care planning for catastrophic injuries</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-gray-400 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
            Take Action Now
          </div>
          <h2 className="text-5xl md:text-6xl font-light mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Hold Negligent Healthcare Providers <em className="italic">Accountable</em>
          </h2>
          <div className="w-16 h-px bg-white mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
            Medical malpractice cases have strict time limits. Contact Jun Law Firm today for a free case evaluation with our medical-legal team.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:+13177012768"
              className="bg-white text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Call: (317) 701-2768
            </a>
            <button 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }, 300);
              }}
              className="border-2 border-white text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Online Contact Form
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
